<template>
  <div class="login-wrapper">
    <div class="login-container">
      <!-- Logo (separate for mobile fixed positioning) -->
      <div class="login-logo">
        <CompanyLogo />
      </div>

      <div class="login-card">
        <!-- Title -->
        <div class="text-center mb-4">
          <h1 class="login-title">Sisene Partner Portaali</h1>
          <p class="login-subtitle">Palun vali sisselogimise viis</p>
        </div>

        <!-- Global Alert (from Vuex) -->
        <b-alert
          v-if="message"
          variant="danger"
          show
          dismissible
          class="alert-custom"
        >
          {{ message }}
        </b-alert>

        <!-- Login Forms and Verification Screen -->
        <transition name="fade" mode="out-in">
          <!-- Login Form (when not verifying) -->
          <div v-if="!verificationCode" key="login-form">
            <!-- Method Selection Buttons -->
            <div class="method-buttons">
              <button
                type="button"
                class="btn method-btn"
                :class="{
                  'method-btn-active': authMethod === 'password',
                  'method-btn-inactive': authMethod !== 'password'
                }"
                @click="selectMethod('password')"
              >
                <i class="mdi mdi-account-key"></i>
                Parool
              </button>
              <button
                type="button"
                class="btn method-btn"
                :class="{
                  'method-btn-active': authMethod === 'smartid',
                  'method-btn-inactive': authMethod !== 'smartid'
                }"
                @click="selectMethod('smartid')"
              >
                <i class="mdi mdi-shield-check-outline"></i>
                Smart-ID
              </button>
              <button
                type="button"
                class="btn method-btn"
                :class="{
                  'method-btn-active': authMethod === 'mid',
                  'method-btn-inactive': authMethod !== 'mid'
                }"
                @click="selectMethod('mid')"
              >
                <i class="mdi mdi-cellphone-key"></i>
                Mobiil-ID
              </button>
            </div>

            <!-- Form Fields Container (fixed height to prevent card movement) -->
            <div class="form-fields-container">
              <transition name="slide-fade" mode="out-in">
              <!-- Password Form Fields -->
              <div v-if="authMethod === 'password'" key="password-form" class="form-content">
                <b-form-group class="form-group-custom">
                  <label for="email" class="form-label">E-posti aadress</label>
                  <b-form-input
                    ref="emailInput"
                    id="email"
                    v-model.trim="email"
                    type="email"
                    placeholder="Sisesta e-posti aadress"
                    required
                    autocomplete="email"
                    class="form-control-custom"
                    :state="validateState('email')"
                    @keyup.enter="handleSubmit"
                  />
                  <b-form-invalid-feedback>
                    {{ $t('VALIDATION.INVALID_EMAIL') }}
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group class="form-group-custom">
                  <label for="password" class="form-label">Parool</label>
                  <b-form-input
                    id="password"
                    v-model.trim="password"
                    type="password"
                    placeholder="Sisesta parool"
                    required
                    autocomplete="current-password"
                    class="form-control-custom"
                    @keyup.enter="handleSubmit"
                  />
                </b-form-group>
              </div>

              <!-- Smart-ID Form Fields -->
              <div v-else-if="authMethod === 'smartid'" key="smartid-form" class="form-content">
                <!-- Error Alert -->
                <b-alert v-if="digitalAuthError" variant="danger" show dismissible @dismissed="digitalAuthError = null" class="alert-custom">
                  {{ digitalAuthError }}
                </b-alert>

                <b-form-group class="form-group-custom">
                  <label for="identityCode" class="form-label">Isikukood</label>
                  <b-form-input
                    ref="identityCodeInput"
                    id="identityCode"
                    v-model="identityCode"
                    placeholder="Sisesta 11-kohaline isikukood"
                    maxlength="11"
                    pattern="[0-9]*"
                    inputmode="numeric"
                    required
                    class="form-control-custom"
                    @keyup.enter="handleSubmit"
                  />
                </b-form-group>
              </div>

              <!-- Mobile-ID Form Fields -->
              <div v-else-if="authMethod === 'mid'" key="mid-form" class="form-content">
                <!-- Error Alert -->
                <b-alert v-if="digitalAuthError" variant="danger" show dismissible @dismissed="digitalAuthError = null" class="alert-custom">
                  {{ digitalAuthError }}
                </b-alert>

                <b-form-group class="form-group-custom">
                  <label for="identityCode2" class="form-label">Isikukood</label>
                  <b-form-input
                    ref="identityCode2Input"
                    id="identityCode2"
                    v-model="identityCode"
                    placeholder="Sisesta 11-kohaline isikukood"
                    maxlength="11"
                    pattern="[0-9]*"
                    inputmode="numeric"
                    required
                    class="form-control-custom"
                    @keyup.enter="handleSubmit"
                  />
                </b-form-group>

                <b-form-group class="form-group-custom">
                  <label for="phoneNumber" class="form-label">Mobiiltelefon</label>
                  <b-input-group>
                    <b-input-group-prepend>
                      <span class="input-group-text">+372</span>
                    </b-input-group-prepend>
                    <b-form-input
                      id="phoneNumber"
                      v-model="phoneNumber"
                      placeholder="Sisesta telefoninumber"
                      maxlength="9"
                      pattern="[0-9]*"
                      inputmode="tel"
                      required
                      class="form-control-custom"
                      @keyup.enter="handleSubmit"
                    />
                  </b-input-group>
                </b-form-group>
              </div>
              </transition>
            </div>

            <!-- Submit Button (Outside transition, stays fixed) -->
            <div class="button-container">
              <b-button
                variant="primary"
                size="lg"
                block
                class="submit-btn"
                :disabled="digitalAuthLoading"
                @click="handleSubmit"
              >
                <b-spinner v-if="digitalAuthLoading" small class="mr-2"></b-spinner>
                {{ digitalAuthLoading ? $t('STARTING') : $t('ENTER') }}
              </b-button>
            </div>
          </div>

          <!-- Verification Screen -->
          <div v-else class="verification-screen text-center" key="verification-screen">
            <h2 class="verification-title">
              <i class="mdi mdi-shield-check-outline"></i>
              Kinnita isik
            </h2>
            <p class="verification-instruction">
              Kui ülaltoodud kood on sama, mida kuvatakse teie seadme ekraanil, sisestage oma PIN1 kood.
            </p>

            <div class="verification-code-display">
              {{ verificationCode }}
            </div>

            <div class="verification-status">
              <b-spinner small variant="light"></b-spinner>
              <span class="ml-2">Ootame kinnitust...</span>
            </div>

            <div class="button-container">
              <b-button
                variant="outline-light"
                @click="resetDigitalAuth"
                class="cancel-btn"
                size="lg"
                block
              >
                <i class="mdi mdi-close"></i>
                Katkesta
              </b-button>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import validateState from '../mixins/validateState';
import { mapState } from 'vuex';
import { startDigitalAuth, checkDigitalAuthStatus } from '../api/digitalAuth';
import { parseJwt } from '../utils';
import CompanyLogo from './icons/CompanyLogo.vue';

const AUTH_TIMEOUT_MS = 90000;
const POLLING_INTERVAL_MS = 2000;

export default {
  name: 'Login',
  components: {
    CompanyLogo,
  },
  mixins: [validateState],
  data() {
    return {
      email: '',
      password: '',
      authMethod: 'password', // Default to password
      identityCode: '',
      phoneNumber: '',
      digitalAuthLoading: false,
      digitalAuthError: null,
      verificationCode: null,
      sessionId: null,
      pollingInterval: null,
      timeoutId: null,
      isProcessingAuth: false,
    };
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    selectMethod(method) {
      if (this.verificationCode) return; // Don't allow changing during verification
      this.authMethod = method;
      this.digitalAuthError = null;

      // Focus appropriate input after transition completes
      this.$nextTick(() => {
        setTimeout(() => {
          if (method === 'password' && this.$refs.emailInput) {
            this.$refs.emailInput.focus();
          } else if (method === 'smartid' && this.$refs.identityCodeInput) {
            this.$refs.identityCodeInput.focus();
          } else if (method === 'mid' && this.$refs.identityCode2Input) {
            this.$refs.identityCode2Input.focus();
          }
        }, 350); // Wait for slide transition (300ms) + buffer
      });
    },

    handleSubmit() {
      if (this.authMethod === 'password') {
        this.login();
      } else {
        this.startDigitalAuth();
      }
    },

    login() {
      this.$store
        .dispatch('login', {
          user: {
            username: this.email,
            password: this.password,
          },
        })
        .then(() => {
          this.email = this.$store.state.alert ? '' : this.email;
          this.password = '';
        });
    },

    async startDigitalAuth() {
      this.digitalAuthLoading = true;
      this.digitalAuthError = null;

      const method = this.authMethod === 'smartid' ? 'smartid' : 'mid';

      try {
        const response = await startDigitalAuth({
          method: method,
          identityCode: this.identityCode,
          phone: this.phoneNumber,
        });

        this.sessionId = response.data.sessionId;
        this.verificationCode = response.data.verificationCode;
        this.digitalAuthLoading = false;

        this.startPolling(method);
      } catch (error) {
        this.digitalAuthLoading = false;
        this.digitalAuthError =
          error.response?.data?.message ||
          'Autentimine ebaõnnestus. Palun proovi uuesti.';
        console.error('Digital auth start failed:', error);
      }
    },

    startPolling(method) {
      this.pollingInterval = setInterval(() => {
        this.checkAuthStatus(method);
      }, POLLING_INTERVAL_MS);

      this.timeoutId = setTimeout(() => {
        this.stopPolling();
        this.digitalAuthError = 'Sisselogimise aeg sai läbi. Palun proovi uuesti.';
        this.verificationCode = null;
      }, AUTH_TIMEOUT_MS);
    },

    async checkAuthStatus(method) {
      // Prevent race condition - don't start new check if one is already in progress
      if (this.isProcessingAuth) {
        return;
      }

      try {
        this.isProcessingAuth = true;
        const response = await checkDigitalAuthStatus(
          this.sessionId,
          method,
          this.phoneNumber
        );

        if (response.data.state === 'COMPLETED') {
          this.stopPolling();
          this.verificationCode = null;

          if (response.data.result === 'OK') {
            const authHeader = response.headers.authorization;

            if (authHeader) {
              const jwtToken = authHeader.replace('Bearer ', '');
              const { exp } = parseJwt(authHeader);
              const role = response.headers.role.replace('ROLE_', '');
              const locale = response.headers.language;

              this.$store.dispatch('completeLogin', { jwtToken, role, exp, locale });
            } else {
              console.error('No authorization header found in response');
              this.digitalAuthError = 'Autentimine ebaõnnestus.';
            }
          } else {
            this.digitalAuthError = this.getErrorMessage(response.data.result);
          }
        }
      } catch (error) {
        console.error('Auth status check failed:', error);
        this.stopPolling();
        this.digitalAuthError = 'Viga sisselogimisel. Palun proovi uuesti.';
        this.verificationCode = null;
      } finally {
        this.isProcessingAuth = false;
      }
    },

    getErrorMessage(result) {
      const messages = {
        ERROR: 'Sisselogimine ebaõnnestus. Palun proovi uuesti.',
        TIMEOUT: 'Sisselogimise aeg sai läbi. Palun proovi uuesti.',
        USER_CANCELLED: 'Kasutaja katkestas sisselogimise.',
        NOT_FOUND: 'Sisestatud andmetega kasutajat ei leitud. Palun kontrolli andmeid.',
        NO_ACCESS: 'Kasutajat ei leitud või ligipääs keelatud. Palun võta ühendust administraatoriga.',
      };
      return messages[result] || messages.ERROR;
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    },

    resetDigitalAuth() {
      this.stopPolling();
      this.verificationCode = null;
      this.digitalAuthError = null;
      this.sessionId = null;
      this.identityCode = '';
      this.phoneNumber = '';
    },
  },
  computed: {
    ...mapState('alert', ['message']),
  },
};
</script>

<style scoped>
.login-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fcfcfc;
  padding: 2rem 1rem;
}

.login-container {
  width: 100%;
  max-width: 500px;
}

.login-card {
  background: white;
  border-radius: 12px;
  padding: 2rem 1.75rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  animation: slideUp 0.4s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Logo */
.login-logo {
  height: 60px;
  width: auto;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
}

.login-logo svg {
  height: 100%;
  width: auto;
  max-width: 100%;
}

/* Title */
.login-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  text-align: center;
}

.login-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 0;
  text-align: center;
}

/* Alerts */
.alert-custom {
  margin-bottom: 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Method Selection Buttons */
.method-buttons {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.method-btn {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.7rem 0.75rem;
  border-radius: 8px;
  border: 2px solid #822460;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.method-btn i {
  font-size: 1.1rem;
}

.method-btn-active {
  background-color: #822460;
  color: white;
  border-color: #822460;
}

.method-btn-inactive {
  background-color: white;
  color: #822460;
  border-color: #822460;
}

.method-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(130, 36, 96, 0.25);
  background-color: #621b48;
  border-color: #621b48;
  color: white;
}

.method-btn:active:not(:disabled) {
  transform: translateY(0);
}

/* Form Content */
.form-content {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Form Groups */
.form-group-custom {
  margin-bottom: 1.25rem;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: #495057;
  margin-bottom: 0.5rem;
  display: block;
}

.form-control-custom {
  border-radius: 6px;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.form-control-custom:focus {
  border-color: #822460;
  box-shadow: 0 0 0 0.15rem rgba(130, 36, 96, 0.12);
}

.form-control-custom::placeholder {
  color: #adb5bd;
}

/* Input Group */
.input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-right: none;
  border-radius: 6px 0 0 6px;
  font-weight: 500;
  color: #495057;
}

.input-group .form-control-custom {
  border-left: none;
  border-radius: 0 6px 6px 0;
}

.input-group .form-control-custom:focus {
  border-left: 1px solid #822460;
}

/* Submit Button */
.submit-btn {
  border-radius: 6px;
  padding: 0.7rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: #822460;
  border-color: #822460;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(130, 36, 96, 0.3);
  background-color: #621b48;
  border-color: #621b48;
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

/* Verification Screen */
.verification-screen {
  padding: 1rem 0;
  animation: fadeIn 0.4s ease-in;
}

.verification-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.verification-title i {
  font-size: 1.5rem;
  color: #822460;
}

.verification-instruction {
  font-size: 0.95rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.verification-code-display {
  background: linear-gradient(135deg, #822460 0%, #621b48 100%);
  color: white;
  padding: 1.75rem 1.5rem;
  border-radius: 10px;
  font-size: 2rem;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.3em;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 24px rgba(130, 36, 96, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 8px 24px rgba(130, 36, 96, 0.3);
  }
  50% {
    box-shadow: 0 8px 32px rgba(130, 36, 96, 0.5);
  }
}

.verification-status {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 0.95rem;
}

.cancel-btn {
  border-radius: 8px;
  padding: 0.75rem;
  font-weight: 600;
  border: 2px solid white;
  background: transparent;
  color: white;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: white;
  color: #822460;
  border-color: white;
}

/* Button Container */
.button-container {
  margin-top: 1.5rem;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}

/* Responsive */
@media (max-width: 576px) {
  .login-wrapper {
    padding: 0;
    /* Keep vertical centering like customer portal */
  }

  .login-container {
    max-width: 100%;
    width: 100%;
    padding-top: 75px; /* Account for fixed logo (70px height) */
  }

  /* Fixed logo at top */
  .login-logo {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 70px;
    margin: 0;
    padding: 0.75rem 1.5rem;
    background: white;
    z-index: 1000;
  }

  .login-logo svg {
    max-width: 100%;
  }

  /* Full-width card on mobile (centered by parent flexbox) */
  .login-card {
    width: 100%;
    max-width: 100%;
    padding: 0.75rem;
    padding-bottom: 70px; /* Space for fixed button at bottom */
    box-shadow: none;
    border-radius: 0;
    /* Let flexbox parent handle vertical centering */
  }

  .login-title {
    font-size: 1.2rem;
    margin-bottom: 1.25rem;
  }

  .method-buttons {
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
  }

  .method-btn {
    padding: 0.65rem;
    font-size: 0.9rem;
  }

  .form-label {
    font-size: 0.85rem;
  }

  .form-control-custom {
    font-size: 0.9rem;
  }

  .verification-code-display {
    font-size: 1.6rem;
    padding: 1.5rem 1rem;
  }

  /* Fixed height for form fields to prevent card movement */
  .form-fields-container {
    min-height: 180px; /* Height of tallest form (Mobile-ID with 2 fields) */
  }

  /* Fix button at bottom on mobile */
  .button-container {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0 !important;
    padding: 0.75rem;
    background: white;
    border-top: 1px solid #e9ecef;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
    z-index: 100;
    padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
  }

  /* When in verification screen, make button work with gradient background */
  .verification-screen .button-container {
    background: transparent;
    border-top: none;
    box-shadow: none;
  }
}

@media (max-width: 375px) {
  .login-container {
    padding-top: 65px; /* Account for smaller fixed logo (60px) */
  }

  /* Smaller fixed logo */
  .login-logo {
    height: 60px;
  }

  .login-card {
    padding: 0.65rem;
  }

  .login-title {
    font-size: 1.15rem;
  }

  .verification-code-display {
    font-size: 1.4rem;
  }
}

@media (max-width: 320px) {
  .login-container {
    padding-top: 65px; /* Account for smaller fixed logo (60px) */
  }

  .login-card {
    padding: 0.5rem;
  }

  .login-title {
    font-size: 1.1rem;
  }

  .method-btn {
    font-size: 0.85rem;
  }
}
</style>
